/**
 * Confetti - Lucky Number Design System
 *
 * Celebration effects using canvas-confetti library.
 * Uses brand colors: gold, electric, petrol.
 */

// Dynamic import to avoid loading confetti unless needed
let confettiModule = null;

async function loadConfetti() {
  if (!confettiModule) {
    const { default: confetti } = await import('canvas-confetti');
    confettiModule = confetti;
  }
  return confettiModule;
}

// Brand colors for confetti
const BRAND_COLORS = [
  '#D4AF37', // gold
  '#E5C65C', // gold-light
  '#3B82F6', // electric
  '#0D4F4F', // petrol
  '#FAF7F2', // cream
];

/**
 * Fire a basic confetti burst
 */
export async function fireConfetti(options = {}) {
  const confetti = await loadConfetti();

  const defaults = {
    particleCount: 100,
    spread: 70,
    origin: { y: 0.6 },
    colors: BRAND_COLORS,
    ...options,
  };

  confetti(defaults);
}

/**
 * Winner celebration - dramatic multi-burst effect
 */
export async function celebrateWinner() {
  const confetti = await loadConfetti();

  const duration = 3000;
  const animationEnd = Date.now() + duration;
  const defaults = {
    startVelocity: 30,
    spread: 360,
    ticks: 60,
    zIndex: 10000,
    colors: BRAND_COLORS,
  };

  function randomInRange(min, max) {
    return Math.random() * (max - min) + min;
  }

  const interval = setInterval(function () {
    const timeLeft = animationEnd - Date.now();

    if (timeLeft <= 0) {
      return clearInterval(interval);
    }

    const particleCount = 50 * (timeLeft / duration);

    // Fire from left and right
    confetti({
      ...defaults,
      particleCount,
      origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 },
    });
    confetti({
      ...defaults,
      particleCount,
      origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 },
    });
  }, 250);
}

/**
 * Gold rain effect for special moments
 */
export async function goldRain() {
  const confetti = await loadConfetti();

  const duration = 2000;
  const animationEnd = Date.now() + duration;

  const goldColors = ['#D4AF37', '#E5C65C', '#B8942F'];

  function frame() {
    confetti({
      particleCount: 2,
      angle: 90,
      spread: 120,
      startVelocity: 45,
      origin: { x: Math.random(), y: -0.1 },
      colors: goldColors,
      shapes: ['circle', 'square'],
      gravity: 1.2,
      scalar: 1.5,
      drift: 0,
      ticks: 200,
      zIndex: 10000,
    });

    if (Date.now() < animationEnd) {
      requestAnimationFrame(frame);
    }
  }

  frame();
}

/**
 * Side cannons - burst from corners
 */
export async function sideCannons() {
  const confetti = await loadConfetti();

  const end = Date.now() + 1000;

  // Left cannon
  (function leftCannon() {
    confetti({
      particleCount: 7,
      angle: 60,
      spread: 55,
      origin: { x: 0, y: 0.65 },
      colors: BRAND_COLORS,
    });

    if (Date.now() < end) {
      requestAnimationFrame(leftCannon);
    }
  })();

  // Right cannon
  (function rightCannon() {
    confetti({
      particleCount: 7,
      angle: 120,
      spread: 55,
      origin: { x: 1, y: 0.65 },
      colors: BRAND_COLORS,
    });

    if (Date.now() < end) {
      requestAnimationFrame(rightCannon);
    }
  })();
}

/**
 * Fireworks effect
 */
export async function fireworks() {
  const confetti = await loadConfetti();

  const duration = 5000;
  const animationEnd = Date.now() + duration;
  const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 10000 };

  function randomInRange(min, max) {
    return Math.random() * (max - min) + min;
  }

  const interval = setInterval(function () {
    const timeLeft = animationEnd - Date.now();

    if (timeLeft <= 0) {
      return clearInterval(interval);
    }

    const particleCount = 50 * (timeLeft / duration);

    confetti({
      ...defaults,
      particleCount,
      origin: { x: randomInRange(0.1, 0.9), y: Math.random() * 0.4 },
      colors: BRAND_COLORS,
    });
  }, 500);
}

export default {
  fireConfetti,
  celebrateWinner,
  goldRain,
  sideCannons,
  fireworks,
};
